/* global document */

import { AriaPopup, composeRefs } from 'localmed-core';
import React, { Children, ReactElement, ReactNode } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import PopoverPanel from './PopoverPanel';
import type { PanelProps, PopoverPlacement } from './types';

type ContentFnProps = {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
};

type ChildrenFnProps = ContentFnProps & {
  triggerProps: any;
};

type ContentFn = (contentFnProps: ContentFnProps) => ReactNode;
type ChildrenFn = (childrenFnProps: ChildrenFnProps) => ReactNode;

export interface Props extends PanelProps {
  id: string;
  bringFocusInside?: boolean;
  content: ReactNode | ContentFn;
  placement?: PopoverPlacement;
  offset?: number;
  children: ReactElement | ChildrenFn;
}

export default function Popover({
  id,
  bringFocusInside = false,
  content,
  placement = 'bottom',
  offset = 9,
  children,
  ...props
}: Props) {
  return (
    <Manager>
      <AriaPopup id={id} bringFocusInside={bringFocusInside}>
        {({ getTriggerProps, getPopupProps, open, close, toggle, isOpen }) => {
          const { ref: ariaTriggerRef, ...triggerProps } = getTriggerProps();
          const { ref: ariaPopupRef, ...popupProps } = getPopupProps();
          return (
            <>
              <Reference>
                {({ ref: popperRef }) => {
                  const triggerPropsWithRef = {
                    ...triggerProps,
                    ref: composeRefs([popperRef, ariaTriggerRef]),
                  };
                  if (typeof children === 'function') {
                    return children({
                      open,
                      close,
                      toggle,
                      isOpen,
                      triggerProps: triggerPropsWithRef,
                    });
                  }
                  return React.cloneElement(Children.only(children), triggerPropsWithRef);
                }}
              </Reference>
              <Popper
                placement={placement}
                modifiers={{
                  offset: {
                    enabled: true,
                    offset: `0, ${offset}`,
                  },
                  computeStyle: {
                    gpuAcceleration: false,
                  },
                  preventOverflow: {
                    boundariesElement: typeof document !== 'undefined' ? document.body : undefined,
                    padding: 12,
                  },
                }}
              >
                {({ ref: popperRef, style, placement: actualPlacement, arrowProps }) => (
                  <PopoverPanel
                    {...props}
                    {...popupProps}
                    isOpen={isOpen}
                    ref={composeRefs([popperRef, ariaPopupRef])}
                    style={style}
                    placement={actualPlacement}
                    arrowProps={arrowProps}
                  >
                    {typeof content === 'function'
                      ? content({ open, close, toggle, isOpen })
                      : content}
                  </PopoverPanel>
                )}
              </Popper>
            </>
          );
        }}
      </AriaPopup>
    </Manager>
  );
}
